import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { User, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hata mesajını düzgün formata çevir
const getErrorMessage = (err, defaultMsg) => {
  const detail = err.response?.data?.detail;
  if (!detail) return err.message || defaultMsg;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || 'Hata').join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || defaultMsg;
  }
  return defaultMsg;
};

const AuthDialog = ({ open, onClose, skipReload = false }) => {
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (timeLeft > 0 && step === 'otp') {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft, step]);

  const handleSendOTP = async () => {
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/send-otp`, { 
        phone_number: phone,
        full_name: mode === 'register' ? fullName : undefined,
        email: mode === 'register' ? email : undefined,
        is_registration: mode === 'register'
      });
      setStep('otp');
      setTimeLeft(600);
    } catch (err) {
      setError(getErrorMessage(err, 'SMS gönderilemedi. Lütfen tekrar deneyin.'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-otp`, {
        phone_number: phone,
        code: otp,
        full_name: mode === 'register' ? fullName : undefined,
        email: mode === 'register' ? email : undefined
      });
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user_data', JSON.stringify(response.data.user));
      
      onClose();
      
      // Redirect URL varsa oraya git, yoksa hesabım sayfasına yönlendir
      const redirectUrl = localStorage.getItem('redirect_after_login');
      if (redirectUrl) {
        localStorage.removeItem('redirect_after_login');
        window.location.href = redirectUrl;
      } else if (!skipReload) {
        // Hesabım sayfasına yönlendir
        window.location.href = '/hesabim';
      }
    } catch (err) {
      setError('Geçersiz kod. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const resetDialog = () => {
    setMode('login');
    setStep('phone');
    setPhone('');
    setOtp('');
    setFullName('');
    setEmail('');
    setError('');
    setTimeLeft(0);
  };

  const isRegisterFormValid = phone.length >= 10;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) { resetDialog(); onClose(); } }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="auth-dialog">
        {step === 'phone' ? (
          <>
            {/* Tab Başlıkları */}
            <div className="flex border-b">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-4 text-center font-semibold transition-colors ${
                  mode === 'login' 
                    ? 'text-[#006D77] border-b-2 border-[#006D77] bg-white' 
                    : 'text-gray-500 bg-gray-50 hover:text-gray-700'
                }`}
              >
                Giriş Yap
              </button>
              <button
                onClick={() => setMode('register')}
                className={`flex-1 py-4 text-center font-semibold transition-colors ${
                  mode === 'register' 
                    ? 'text-[#006D77] border-b-2 border-[#006D77] bg-white' 
                    : 'text-gray-500 bg-gray-50 hover:text-gray-700'
                }`}
              >
                Üye Ol
              </button>
            </div>

            <div className="p-6 space-y-4">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-center">
                  {mode === 'login' ? 'Hoş Geldiniz!' : 'Hesap Oluşturun'}
                </DialogTitle>
                <p className="text-gray-500 text-sm text-center mt-1">
                  {mode === 'login' 
                    ? 'Telefon numaranızla giriş yapın' 
                    : 'Bilgilerinizi girerek üye olun'}
                </p>
              </DialogHeader>

              {mode === 'register' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-sm font-medium">Ad Soyad (Opsiyonel)</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="fullName"
                        type="text"
                        placeholder="Adınız Soyadınız"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="pl-10 rounded-xl h-12"
                        data-testid="fullname-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">E-posta (Opsiyonel)</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="ornek@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 rounded-xl h-12"
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">Telefon Numarası</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <span className="absolute left-10 top-1/2 -translate-y-1/2 text-gray-700 font-medium">0</span>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(5XX) XXX XX XX"
                    value={(() => {
                      const digits = phone.replace(/\D/g, '').slice(1); // Remove leading 0
                      if (digits.length === 0) return '';
                      if (digits.length <= 3) return `(${digits}`;
                      if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
                      if (digits.length <= 8) return `(${digits.slice(0,3)}) ${digits.slice(3,6)} ${digits.slice(6)}`;
                      return `(${digits.slice(0,3)}) ${digits.slice(3,6)} ${digits.slice(6,8)} ${digits.slice(8,10)}`;
                    })()}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 10) setPhone('0' + value);
                    }}
                    className="pl-14 rounded-xl h-12 font-mono tracking-wide"
                    maxLength={17}
                    data-testid="phone-input"
                  />
                </div>
                <p className="text-xs text-gray-500">Başında 0 olmadan numaranızı girin</p>
              </div>

              {error && <p className="text-sm text-red-600 text-center bg-red-50 py-2 rounded-lg">{error}</p>}
              
              <Button
                onClick={handleSendOTP}
                disabled={loading || !isRegisterFormValid}
                className="w-full bg-[#006D77] hover:bg-[#005861] text-white rounded-xl h-12 font-semibold"
                data-testid="send-otp-button"
              >
                {loading ? 'Gönderiliyor...' : 'Devam Et'}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                Devam ederek <a href="/kullanim-kosullari" className="text-[#006D77] hover:underline">Kullanım Koşulları</a>&apos;nı ve <a href="/gizlilik" className="text-[#006D77] hover:underline">Gizlilik Politikası</a>&apos;nı kabul etmiş olursunuz.
              </p>
            </div>
          </>
        ) : (
          <div className="p-6 space-y-4">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-center">Kodu Doğrula</DialogTitle>
            </DialogHeader>

            <div className="text-center">
              <div className="bg-[#E0F2F4] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-[#006D77]" />
              </div>
              <p className="text-sm text-gray-600">
                <span className="font-semibold">{phone}</span> numarasına gönderilen 6 haneli kodu girin.
              </p>
            </div>

            <div className="space-y-2">
              <Input
                id="otp"
                type="text"
                placeholder="• • • • • •"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="rounded-xl text-center text-2xl tracking-[0.5em] h-14 font-mono"
                maxLength={6}
                data-testid="otp-input"
              />
            </div>

            <div className="flex items-center justify-center gap-2 text-sm">
              <div className="w-2 h-2 bg-[#006D77] rounded-full animate-pulse"></div>
              <span className="text-gray-600">
                Kalan süre: <span className="font-semibold text-[#006D77]">{Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}</span>
              </span>
            </div>

            {error && <p className="text-sm text-red-600 text-center bg-red-50 py-2 rounded-lg">{error}</p>}

            <div className="space-y-3">
              <Button
                onClick={handleVerifyOTP}
                disabled={loading || otp.length !== 6 || timeLeft === 0}
                className="w-full bg-[#006D77] hover:bg-[#005861] text-white rounded-xl h-12 font-semibold"
                data-testid="verify-otp-button"
              >
                {loading ? 'Doğrulanıyor...' : 'Doğrula ve Giriş Yap'}
              </Button>
              <Button
                onClick={() => setStep('phone')}
                variant="ghost"
                className="w-full text-gray-600 hover:text-gray-800"
                data-testid="back-button"
              >
                ← Geri Dön
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              Kod gelmedi mi? <button onClick={handleSendOTP} className="text-[#006D77] font-semibold hover:underline" disabled={loading}>Tekrar Gönder</button>
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
